import React, { Fragment, useEffect, useRef, useState } from "react";
import { ReactSVG } from "react-svg";
import I18n from "i18n-js";

import { MIME_TYPES } from "../config/mime_types";

import { getReadableFileSize } from "../services/file";

import documentUpload from "icons/document-upload.svg";
import documentSelected from "icons/document-selected.svg";
import infoIcon from "icons/info-indicator.svg";

const MAX_FILE_SIZE_MB = 5;

const FileChooser = ({
  disabled,
  supportedFiles,
  maxFileSize = MAX_FILE_SIZE_MB,
  onFileSelected
}) => {

  const [file, setFile] = useState(null);

  const uploaderRef = useRef();

  useEffect(() => {
    function preventsDefault(event) {
      event.preventDefault();
      event.stopPropagation();
    }

    function handleDrop(event) {
      if (disabled) return;

      const dt = event.dataTransfer;
      const file = dt.files?.length ? dt.files[0] : null;
      selectFile(file);
    }

    // Prevents default drag behaviors
    ["dragenter", "dragover", "dragleave", "drop"].forEach(event => {
      uploaderRef.current?.addEventListener(event, preventsDefault, false);
    });

    // Highlight drop zone when dragging over
    ["dragenter", "dragover"].forEach(event => {
      const uploader = uploaderRef.current;
      uploader?.addEventListener(event, () => uploader?.classList.add("hover"), false);
    });

    ["dragleave", "drop"].forEach(event => {
      const uploader = uploaderRef.current;
      uploader?.addEventListener(event, () => uploader?.classList.remove("hover"), false);
    });

    // Handle dropped files
    uploaderRef.current?.addEventListener("drop", handleDrop, false);

    return () => {
      ["dragenter", "dragover", "dragleave", "drop"].forEach(event => {
        uploaderRef.current?.removeEventListener(event, preventsDefault, false);
      });
    }
  }, []);

  const isValid = (file) => {
    return file && file.size <= maxFileSize * 1024 * 1024;
  }

  const selectFile = (file) => {
    isValid(file) && onFileSelected && onFileSelected(file);
    setFile(file);
  }

  const showFileChooser = async () => {
    return new Promise((resolve, reject) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = supportedFiles;
  
      input.onchange = event => {
        const file = event.target.files[0];

        if (file) {
          resolve(file);
        } else {
          reject(new Error("No file selected!"))
        }
      }
  
      input.click();
    });
  }

  const handleUploaderClick = async () => {
    if (disabled) return;
    const file = await showFileChooser();
    selectFile(file);
  }

  return (
    <div className="file-chooser">
      <div
        ref={uploaderRef}
        className={`uploader ${file ? "selected" : null} ${disabled ? "disabled" : null}`}
        onClick={handleUploaderClick}
      >
        {file ? (
          <Fragment>
            <ReactSVG className="icon" src={documentSelected} />
            <span>{file.name}</span>
            <span className="stats">{MIME_TYPES[file.type]} - {getReadableFileSize(file.size)}</span>
          </Fragment>
        ) : (
          <Fragment>
            <ReactSVG className="icon" src={documentUpload} />
            <span>
              {I18n.t("common.components.file_chooser.drop")}&nbsp;
              <span className="link">{I18n.t("common.components.file_chooser.link")}</span>
            </span>
          </Fragment>
        )}
      </div>
      <div className="file-chooser-footer">
        <div className="file-chooser-row">
          <ReactSVG className="icon" src={infoIcon} />
          <span>{I18n.t("common.components.file_chooser.supported_files")} {supportedFiles}</span>
        </div>
        <div className="file-chooser-row">
          <span>{I18n.t("common.components.file_chooser.max_file_size")} {maxFileSize}mb</span>
        </div>
      </div>
    </div>
  );
}

export default FileChooser;
